import { getRegion, type Region } from './regions';
import { loadSession, saveSession, type Session } from './session';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: any,
  ) {
    super(message);
  }
}

let refreshing: Promise<Session | null> | null = null;

const refresh = async (): Promise<Session | null> => {
  const s = loadSession();
  if (!s?.refreshToken) return null;
  const res = await fetch(`${getRegion().apiBase}/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken: s.refreshToken }),
  });
  if (!res.ok) {
    saveSession(null);
    return null;
  }
  const next = (await res.json()).session as Session;
  saveSession(next);
  return next;
};

/** JSON fetch against the selected region's control plane; refreshes the session once on 401. */
export const api = async <T = any>(path: string, init: { method?: string; body?: unknown; auth?: boolean } = {}): Promise<T> => apiAt<T>(getRegion(), path, init);

/** Same as `api`, against a specific region (sign-in is shared, so the token works in every region). */
export const apiAt = async <T = any>(region: Region, path: string, init: { method?: string; body?: unknown; auth?: boolean } = {}): Promise<T> => {
  const send = (token?: string) =>
    fetch(`${region.apiBase}${path}`, {
      method: init.method ?? (init.body === undefined ? 'GET' : 'POST'),
      headers: {
        ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });

  let session = init.auth === false ? null : loadSession();
  if (session && session.expiresAt - 60 < Date.now() / 1000) session = await (refreshing ??= refresh().finally(() => (refreshing = null)));
  let res = await send(session?.accessToken);
  if (res.status === 401 && session && init.auth !== false) {
    const next = await (refreshing ??= refresh().finally(() => (refreshing = null)));
    if (next) res = await send(next.accessToken);
  }
  if (res.status === 401 && init.auth !== false) window.dispatchEvent(new Event('am:signed-out'));
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) throw new ApiError(res.status, data?.error?.message ?? `HTTP ${res.status}`, data?.error?.details);
  return data as T;
};

/**
 * POST to a Server-Sent Events endpoint and call `onEvent` for every `data:`
 * frame as it arrives. Resolves when the stream ends.
 */
export const apiStream = async (url: string, body: unknown, onEvent: (e: Record<string, any>) => void, signal?: AbortSignal): Promise<void> => {
  let session = loadSession();
  if (session && session.expiresAt - 60 < Date.now() / 1000) session = await (refreshing ??= refresh().finally(() => (refreshing = null)));
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...(session ? { authorization: `Bearer ${session.accessToken}` } : {}) },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) throw new ApiError(res.status, `Stream failed: HTTP ${res.status}`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let i: number;
    while ((i = buf.indexOf('\n\n')) !== -1) {
      const frame = buf.slice(0, i);
      buf = buf.slice(i + 2);
      for (const line of frame.split('\n')) if (line.startsWith('data: ')) onEvent(JSON.parse(line.slice(6)));
    }
  }
};
